import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import tokenStore from '../../utils/tokenStore';
import { API_URL } from '../../constants/api';

const API = `${API_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GSI_SCRIPT_ID = 'google-gsi-client';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

// Safari (desktop) and every iOS browser (all forced onto WebKit) enforce
// Intelligent Tracking Prevention, which blocks the popup button's
// third-party iframe — it just silently fails to render, no error. Those
// browsers get the redirect-mode flow instead; everyone else keeps the
// popup since it's a nicer in-page experience.
const isITPBrowser = () => {
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS || isSafari;
};

// Same-origin path — Vercel proxies this to the backend (see
// frontend/vercel.json) so the g_csrf_token cookie GSI sets before
// redirecting is actually present when Google posts back to it.
const GOOGLE_REDIRECT_LOGIN_URI = `${window.location.origin}/api/auth/google/redirect`;

/**
 * Drop-in "Continue with Google" button.
 *
 * Loads the Google Identity Services script, renders the official button,
 * exchanges the returned ID token for our own JWT via POST /auth/google,
 * then calls onSuccess(user) just like LoginForm / RegisterForm do.
 *
 * On ITP browsers (Safari, all of iOS) it instead uses ux_mode: 'redirect' —
 * Google posts the credential straight to the backend as a top-level form
 * submission, which sets the same refresh-token cookie /auth/login uses and
 * redirects back here; the app's existing session-restore-on-load logic
 * picks it up, so there's no callback handling needed on this end for that
 * path.
 *
 * Renders nothing if REACT_APP_GOOGLE_CLIENT_ID is not configured.
 */
const GoogleSignInButton = ({ onSuccess, mode = 'signin', disabled = false }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // After the redirect-mode round trip, the backend sends us back here with
  // ?google_error=<reason> on failure (success carries no param — the user
  // is just already logged in). Surface it once, then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('google_error');
    if (!reason) return;
    setError(
      reason === 'csrf'
        ? 'Google sign-in session expired. Please try again.'
        : 'Google sign-in failed. Please try again.'
    );
    params.delete('google_error');
    const rest = params.toString();
    window.history.replaceState({}, document.title, window.location.pathname + (rest ? `?${rest}` : ''));
  }, []);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;

    let cancelled = false;

    const handleCredentialResponse = async (response) => {
      if (!response?.credential) {
        setError('Google did not return a credential. Please try again.');
        return;
      }
      setSubmitting(true);
      setError('');
      try {
        const res = await axios.post(`${API}/auth/google`, { credential: response.credential });
        if (res.data?.access_token) {
          tokenStore.set(res.data.access_token);
          try {
            const userRes = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${res.data.access_token}` },
            });
            await onSuccess?.(userRes.data);
          } catch {
            await onSuccess?.();
          }
        } else {
          setError('Sign-in failed. Please try again.');
        }
      } catch (err) {
        let msg = 'Google sign-in failed. Please try again.';
        if (err.response?.data?.detail) msg = err.response.data.detail;
        setError(msg);
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    };

    const renderButton = () => {
      if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;
      try {
        const useRedirect = isITPBrowser();
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          auto_select: false,
          ...(useRedirect
            ? { ux_mode: 'redirect', login_uri: GOOGLE_REDIRECT_LOGIN_URI }
            : { ux_mode: 'popup', callback: handleCredentialResponse }),
        });
        // Clear any previous render (e.g. on React StrictMode double-mount)
        containerRef.current.innerHTML = '';
        // Google's button needs an explicit pixel width (no percentage option),
        // so measure the actual available space instead of hardcoding one —
        // a fixed width wider than the screen forces the surrounding grid
        // column (and the whole page) to expand to fit it on narrow phones,
        // which is what caused the horizontal overflow/misalignment on mobile.
        const available = containerRef.current.clientWidth || 320;
        const buttonWidth = Math.max(200, Math.min(400, Math.floor(available)));
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          // 'medium' (not 'large') keeps the button on the generic
          // "Sign in/up with Google" label — 'large' lets Google swap it
          // for a personalized "Sign in as <name> <email>" button whenever
          // the device already has an active Google session.
          size: 'medium',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: buttonWidth,
        });
      } catch (e) {
        // GSI throws synchronously if client_id is malformed
        setError('Google sign-in is unavailable right now.');
      }
    };

    const existingScript = document.getElementById(GSI_SCRIPT_ID);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = GSI_SCRIPT_ID;
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', renderButton);
      document.head.appendChild(script);
    } else if (window.google?.accounts?.id) {
      // Script already loaded and google SDK is ready — render immediately.
      renderButton();
    } else {
      // Script tag exists but hasn't finished loading yet — a previous mount
      // added the tag and then unmounted before it loaded (cancelled = true on
      // that mount's cleanup), so the onload from that mount was a no-op.
      // Attach a fresh listener; renderButton checks `cancelled` first so any
      // stale fire after this effect's own cleanup is harmless.
      existingScript.addEventListener('load', renderButton);
    }

    return () => {
      cancelled = true;
    };
  }, [mode, onSuccess]);

  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <div className="fp-google-signin" style={{ marginTop: 4, marginBottom: 4, maxWidth: '100%', boxSizing: 'border-box' }}>
      <div
        ref={containerRef}
        aria-disabled={disabled || submitting}
        style={{
          display: 'flex',
          justifyContent: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
          opacity: disabled || submitting ? 0.6 : 1,
          pointerEvents: disabled || submitting ? 'none' : 'auto',
        }}
      />
      {submitting && (
        <p style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted, #888)', textAlign: 'center' }}>
          Signing you in…
        </p>
      )}
      {error && (
        <p role="alert" style={{ marginTop: 8, fontSize: 12, color: '#f87171', textAlign: 'center' }}>
          {error}
        </p>
      )}
    </div>
  );
};

export default GoogleSignInButton;
